import React, { useEffect, useRef, useState } from 'react';
import { useCall } from '../../context/CallContext';
import { useAuth } from '../../context/AuthContext';
import './Calls.css';

// Carga external_api.js de Jitsi una sola vez (memoizada en window)
function ensureJitsiScript() {
    if (window.JitsiMeetExternalAPI) return Promise.resolve();
    if (window.__jitsiScriptPromise) return window.__jitsiScriptPromise;
    window.__jitsiScriptPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = '/meet/external_api.js';
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('No se pudo cargar el motor de videollamadas'));
        document.body.appendChild(script);
    });
    return window.__jitsiScriptPromise;
}

const JITSI_DOMAIN = (() => {
    if (typeof window === 'undefined') return 'localhost';
    // El Jitsi server vive bajo /meet/ del mismo origin
    return `${window.location.host}/meet`;
})();

// Llamada 1:1: sin "silenciar a todos" ni "levantar la mano"
const TOOLBAR = [
    'microphone', 'camera', 'desktop', 'fullscreen', 'fodeviceselection',
    'hangup', 'chat', 'settings', 'videoquality', 'tileview',
];

// Si el otro sale de la sala y no vuelve en este tiempo, se cuelga. Lo normal es que
// el server avise antes (call_ended); esto cubre que su app muera sin avisar. Un
// microcorte o un cambio de red lo saca y lo vuelve a meter en segundos.
const ESPERA_REGRESO_MS = 20000;

const INTERFACE_OVERRIDES = {
    SHOW_JITSI_WATERMARK: false,
    SHOW_WATERMARK_FOR_GUESTS: false,
    SHOW_BRAND_WATERMARK: false,
    SHOW_POWERED_BY: false,
    JITSI_WATERMARK_LINK: '',
    DEFAULT_LOGO_URL: '',
    DEFAULT_WELCOME_PAGE_LOGO_URL: '',
    APP_NAME: 'Centro Sura',
    NATIVE_APP_NAME: 'Centro Sura',
    PROVIDER_NAME: 'Centro Sura',
    DISABLE_JOIN_LEAVE_NOTIFICATIONS: true,
    HIDE_DEEP_LINKING_LOGO: true,
    MOBILE_APP_PROMO: false,
    DISPLAY_WELCOME_PAGE_CONTENT: false,
    DISPLAY_WELCOME_FOOTER: false,
    SHOW_CHROME_EXTENSION_BANNER: false,
    TOOLBAR_BUTTONS: TOOLBAR,
};

const CONFIG_OVERRIDES = (callType) => ({
    defaultLanguage: 'es',
    startWithVideoMuted: callType === 'audio',
    startWithAudioMuted: false,
    disableThirdPartyRequests: true,
    prejoinPageEnabled: false,
    enableWelcomePage: false,
    enableClosePage: false,
    disableDeepLinking: true,
    disableInviteFunctions: true,
    hideConferenceSubject: true,
    hideConferenceTimer: false,
    hideRecordingLabel: true,
    requireDisplayName: false,
    p2p: { enabled: true },
    toolbarButtons: TOOLBAR,
});

export default function CallView() {
    const { status, call, leaveCall, reportJoined } = useCall();
    const { user } = useAuth();
    const containerRef = useRef(null);
    const apiRef = useRef(null);
    const [error, setError] = useState(null);

    const visible = status === 'in_call' && call?.room_uuid;

    // Por ref: si cambian a mitad de llamada (p. ej. se refresca el usuario o se
    // reconecta el socket) no hay que volver a montar Jitsi y cortar la llamada.
    const latest = useRef({});
    latest.current = { user, leaveCall, reportJoined };

    useEffect(() => {
        if (!visible) return undefined;

        let cancelled = false;
        let api = null;
        let esperaRegreso = null;

        ensureJitsiScript()
            .then(() => {
                if (cancelled || !containerRef.current) return;
                const u = latest.current.user;
                const displayName = u?.nombre_empleado || u?.username || 'Usuario';

                // External API hardcodea https:// en el iframe src y no expone
                // opcion para forzar http. En dev servimos por HTTP, asi que
                // monkey-patch del setter de iframe.src para reescribir https→http
                // ANTES de que el browser haga la request. Restaurado al instante
                // despues de instanciar la API. En prod con HTTPS real es no-op.
                const isDevHttp = window.location.protocol === 'http:';
                let originalDescriptor = null;
                if (isDevHttp) {
                    originalDescriptor = Object.getOwnPropertyDescriptor(HTMLIFrameElement.prototype, 'src')
                        || Object.getOwnPropertyDescriptor(HTMLElement.prototype, 'src');
                    if (originalDescriptor && originalDescriptor.set) {
                        Object.defineProperty(HTMLIFrameElement.prototype, 'src', {
                            configurable: true,
                            get() { return originalDescriptor.get.call(this); },
                            set(value) {
                                if (typeof value === 'string' && value.startsWith('https://') && value.includes(window.location.host)) {
                                    value = value.replace(/^https:/, 'http:');
                                }
                                originalDescriptor.set.call(this, value);
                            }
                        });
                    }
                }

                try {
                    api = new window.JitsiMeetExternalAPI(JITSI_DOMAIN, {
                        roomName: call.room_uuid,
                        jwt: call.jitsi_jwt || undefined,
                        parentNode: containerRef.current,
                        width: '100%',
                        height: '100%',
                        userInfo: { displayName },
                        configOverwrite: CONFIG_OVERRIDES(call.call_type),
                        interfaceConfigOverwrite: INTERFACE_OVERRIDES,
                    });
                } finally {
                    // Restaurar el setter original para no afectar otros iframes
                    if (isDevHttp && originalDescriptor) {
                        Object.defineProperty(HTMLIFrameElement.prototype, 'src', originalDescriptor);
                    }
                }

                api.addListener('videoConferenceJoined', () => {
                    latest.current.reportJoined();
                });
                // videoConferenceLeft dispara cuando el usuario aprieta colgar,
                // ANTES de que Jitsi navegue a su pantalla de "Thank you...".
                // Hacemos dispose del API para arrancar el iframe del DOM antes
                // de que esa pantalla llegue a mostrarse.
                const closeAndLeave = () => {
                    clearTimeout(esperaRegreso);
                    try { api && api.dispose(); } catch { /* noop */ }
                    apiRef.current = null;
                    latest.current.leaveCall();
                };
                api.addListener('videoConferenceLeft', closeAndLeave);
                api.addListener('readyToClose', closeAndLeave);
                api.addListener('participantLeft', () => {
                    clearTimeout(esperaRegreso);
                    esperaRegreso = setTimeout(() => {
                        if (!cancelled && api.getNumberOfParticipants() <= 1) closeAndLeave();
                    }, ESPERA_REGRESO_MS);
                });
                api.addListener('participantJoined', () => clearTimeout(esperaRegreso));

                apiRef.current = api;
            })
            .catch((err) => {
                if (cancelled) return;
                setError(err.message || 'Error cargando videollamada');
            });

        // Si el browser se cierra/recarga durante la llamada, avisar al server
        const onBeforeUnload = () => latest.current.leaveCall();
        window.addEventListener('beforeunload', onBeforeUnload);

        return () => {
            cancelled = true;
            clearTimeout(esperaRegreso);
            window.removeEventListener('beforeunload', onBeforeUnload);
            if (api) {
                try { api.dispose(); } catch { /* noop */ }
            }
            apiRef.current = null;
        };
    }, [visible, call?.room_uuid, call?.call_type]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!visible) return null;

    return (
        <div className="call-fullscreen">
            {error ? (
                <div className="call-error">
                    <p>{error}</p>
                    <button onClick={leaveCall}>Cerrar</button>
                </div>
            ) : (
                <div ref={containerRef} className="call-jitsi-container" />
            )}
        </div>
    );
}
